import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const ADMIN_ROLES = ['administrator', 'admin'];

/**
 * This component updates the administrator permissions in Firebase with complete RBAC structure
 * It runs only when the current user is an administrator (has a users doc with role admin/administrator).
 * Prevents "Missing or insufficient permissions" for contractors/agents who have no write access to roles.
 */
const AdminPermissionUpdater = () => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    const updateAdminPermissions = async () => {
      if (!auth.currentUser) {
        setStatus('idle');
        return;
      }

      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        const role = userSnap.exists() ? (userSnap.data().role || '').toLowerCase() : '';
        if (!ADMIN_ROLES.some((r) => role === r)) {
          setStatus('idle');
          return;
        }

        setStatus('updating');
        console.log('Updating administrator permissions in Firebase with complete RBAC structure...');
        
        // Get the current administrator role document
        const adminRoleRef = doc(db, 'roles', 'administrator');
        const adminRoleDoc = await getDoc(adminRoleRef);
        
        // Complete administrator permissions - matches initialize-role-permissions.js
        const defaultPermissions = {
          features: {
            // Dashboard
            viewDashboard: true,
            // Properties
            viewProperties: true,
            createProperties: true,
            editProperties: true,
            deleteProperties: true,
            viewAllProperties: true,
            // Finance
            viewFinance: true,
            editFinance: true,
            viewPayments: true,
            managePayments: true,
            // Maintenance
            viewMaintenance: true,
            createMaintenance: true,
            updateMaintenance: true,
            manageMaintenance: true,
            // Tenants
            viewTenants: true,
            viewAllTenants: true,
            createTenants: true,
            editTenants: true,
            deleteTenants: true,
            // Landlords/Clients
            viewLandlords: true,
            viewAllLandlords: true,
            createLandlords: true,
            editLandlords: true,
            deleteLandlords: true,
            // Communication
            sendMessages: true,
            viewMessages: true,
            viewNotifications: true,
            // Links
            viewLinks: true,
            manageLinks: true,
            // Documents
            viewDocuments: true,
            uploadDocuments: true,
            deleteDocuments: true,
            viewCertificates: true,
            manageCertificates: true,
            // Calendar
            viewCalendar: true,
            manageCalendar: true,
            // Reports
            viewReports: true,
            exportData: true,
            // Archive
            viewArchive: true,
            manageArchive: true,
            // Users
            viewUsers: true,
            createUsers: true,
            editUsers: true,
            deleteUsers: true,
            manageAgents: true,
            // System
            accessSettings: true,
            manageRoles: true,
            systemConfig: true,
            viewAuditLogs: true,
            manageBranding: true,
            importExportData: true
          },
          pages: {
            dashboard: true,
            properties: true,
            finance: true,
            reports: true,
            documents: true,
            tenants: true,
            landlords: true,
            settings: true,
            maintenance: true,
            calendar: true,
            messages: true,
            links: true,
            archive: true,
            archivedProperties: true,
            archivedTenants: true,
            users: true,
            audit: true,
            roles: true,
            system: true,
            permissions: true,
            notifications: true,
            payments: true,
            certificates: true,
            profile: true,
            account: true
          }
        };
        
        // If the document exists, ensure all permissions are set to true
        if (adminRoleDoc.exists()) {
          console.log('Existing administrator role document found');
          const currentData = adminRoleDoc.data();
          console.log('Current admin role data:', currentData);
          
          // Create a deep copy of the complete permissions
          const completePermissions = JSON.parse(JSON.stringify(defaultPermissions));
          
          // Ensure all features are enabled (administrator gets everything)
          Object.keys(completePermissions.features).forEach(feature => {
            completePermissions.features[feature] = true;
          });
          
          // Ensure all pages are enabled (administrator gets everything)
          Object.keys(completePermissions.pages).forEach(page => {
            completePermissions.pages[page] = true;
          });
          
          console.log('Complete permissions to save (54 features, 24 pages):', completePermissions);
          
          // Create the update data
          const updateData = {
            permissions: completePermissions,
            updatedAt: new Date()
          };
          
          // Update the document
          await updateDoc(adminRoleRef, updateData);
          
          // Verify the update
          const verifyDoc = await getDoc(adminRoleRef);
          console.log('Verified admin role after update:', verifyDoc.data());
          
          console.log('Updated existing administrator role permissions with complete RBAC structure');
        } else {
          console.log('No administrator role document found, creating new one');
          // Create a new document
          const newRoleData = {
            name: 'administrator',
            permissions: defaultPermissions,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          await setDoc(adminRoleRef, newRoleData);
          
          // Verify the creation
          const verifyDoc = await getDoc(adminRoleRef);
          console.log('Verified new admin role document:', verifyDoc.data());
          
          console.log('Created new administrator role permissions with complete RBAC structure');
        }
        
        console.log('Administrator permissions successfully updated with 54 features and 24 pages!');
        setStatus('success');
      } catch (err) {
        console.error('Error updating administrator permissions:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    updateAdminPermissions();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(() => {
      updateAdminPermissions();
    });

    return () => unsubscribe();
  }, []);

  // This component renders a hidden status indicator
  return (
    <div style={{ display: 'none' }}>
      {status === 'success' && (
        <div>Administrator permissions updated successfully with complete RBAC (54 features, 24 pages)!</div>
      )}
      {status === 'error' && (
        <div>Error updating administrator permissions: {error}</div>
      )}
    </div>
  );
};

export default AdminPermissionUpdater;
